<template lang="pug">
	.ssml-insertion--voice-conversion-tool
		Popup(v-if="popupVisible", :popupVisible.sync="popupVisible", :width="740", :height="480", :autosize="true", :title="$tCaption('Voice conversion')", @hiding="close")
			.ssml-insertion--voice-conversion.flex-col.space
				template(v-if="!supported")
					.flex-col.space-large
						.font-big {{ $tCaption('Current voice doesn\' support voice conversion. Please select supported voice from the list below.') }}
						.flex-col.field
							.font-small.upper.grey {{$tCaption('Voice:')}}
							VoicesSelector(
								ref="voiceSelector",
								:audioContext="recorder.audioContext",
								:voice="voiceId",
								:language="locale",
								@selected-voice="onVoiceSelect",
								:filter="v => v && v.voice && v.voice.VoiceConversion"
							)
				.flex-row.align-center.center.space-medium(v-else)
					.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space(@click="toggleRecord", :class="{ recording: recorder.isRecording, 'disabled-controls': recorder.processing }")
						fas(size="lg", :icon="recorder.processing && !uploadedFile ? 'spinner' : (recorder.isRecording ? 'square' : 'fas fa-microphone fa-xs')", :spin="recorder.processing && !uploadedFile")
						span {{recorder.processing && !uploadedFile ? $tCaption('Processing') : $tCaption('Record')}}
					.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="uploadFile", :title="$tCaption('Upload audio')", :class="{ 'disabled-controls': recorder.processing }")
						fas(size="lg", :icon="recorder.processing && uploadedFile ? 'spinner' : 'upload'", :spin="recorder.processing && uploadedFile")
						span {{recorder.processing && uploadedFile ? $tCaption('Processing') : $tCaption('Upload')}}
					template(v-if="!recorder.processing")
						.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space(v-if="sourceBase64", @click="toggleSourcePlay")
							fas(size="lg", :icon="isSourcePlaying === 1 ? 'spinner' : (isSourcePlaying === 2 ? 'square' : 'fas fa-play fa-xs')", :spin="isSourcePlaying === 1")
							span {{isSourcePlaying > 0 ? $tCaption('Stop', '@actions') : $tCaption('Play recorded audio')}}
						.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space(v-if="ssmlBase64", @click="togglePlay")
							fas(size="lg", :icon="isPlaying === 1 ? 'spinner' : (isPlaying === 2 ? 'square' : 'fas fa-play fa-xs')", :spin="isPlaying === 1")
							span {{isPlaying > 0 ? $tCaption('Stop', '@actions') : $tCaption('Play generated audio')}}
			template(v-if="supported", slot="actions-right")
				DxButton(text="Save", type="default", @click="() => save()")
				DxButton(text="Cancel", @click="() =>{ stopRecording(true); popupVisible = false; }")
			template(v-if="supported", slot="actions-left")
				.flex-row.align-center.space-large
					DxCheckBox(:value.sync="recorder.listeningMic.value", :text="$tCaption('Mic preview')")
					VuMeter(ref="vumeter", orientation="horizontal", :isPlaying="recorder.isRecording || recorder.listeningMic.value", :filters="{ stereoAnalyser: recorder.stereoAnalyser }", :compact="true", size="180px")
		FileUploader(ref="fileUploader", :isUIHidden="true", @upload="onFileUpload", :accept="['.wav', '.mp3', '.ogg', '.riff']")
</template>

<script>
	import Popup from 'app-core/Popup.vue';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import storage from 'storage';
	import recorderWorker from 'worker-loader!recorder/recorderWorker.js';
	import Recorder from 'recorder/recorder';
	import audioHelper from 'view/audioTracker/audio-helper';
	import FileUploader from 'app-core/FileUploader.vue';
	import VuMeter from 'view/audioTracker/AudioTrackerVUMeter.vue';
	import VoicesSelector from 'view/insertion/VoicesSelector.vue';

	export default {
		translationCategory: 'insertion.SSMLVoiceConversionTool',
		name: 'ssml-insertion--voice-conversion',
		props: ['voice'],
		components: { Popup, DxButton, DxCheckBox, FileUploader, VuMeter, VoicesSelector },
		data() {
			return {
				popupVisible: false,
				recorder: {
					audioContext: new AudioContext(),
					instance: null,
					stream: null,
					processing: false,
					listeningMic: storage.prop('pronunciation_listeningMic', void 0, false),
					isRecording: false,
					input: null,
					stereoAnalyser: null
				},
				isLoading: 0,
				isPlaying: 0,
				sourceBase64: null,
				isSourcePlaying: 0,
				ssml: null,
				ssmlBase64: null,
				audioLink: null,
				uploadedFile: null,
				voiceId: null,
				locale: null
			}
		},
		computed: {
			supported() {
				return this.voice?.voice?.VoiceConversion;
			}
		},
		watch: {
			'recorder.listeningMic.value'() {
				this.initRecording(!this.recorder.isRecording);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.ssmlVoiceConversionTool = this;
		},
		destroyed() {
			this.stopRecording(true);
		},
		methods: {
			onVoiceSelect(v) {
				this.$emit('voice-change', v);
			},
			async uploadFile() {
				this.$refs.fileUploader.showPopup();
			},
			async onFileUpload(files) {
				if (__DEBUG__)
					console.debug('OnFileUpload', { files, f: files[0], resampled });
				
				this.uploadedFile = files[0];
				const resampled = await audioHelper.resampleBlob(files[0], 16000, { mono: true });
				await this.processBlob(resampled);
			},
			async togglePlay() {
				if (this.isPlaying) {
					this.stopAudio();
					this.isPlaying = 0;
					return;
				}

				try {
					this.isPlaying = 2;
					this.$player = new Audio();
					this.$player.src = this.ssmlBase64;
					this.$player.play();
					this.$player.addEventListener('ended', () => {
						delete this.$player;
						this.isPlaying = 0;
					}, false);
				} catch {
					this.isPlaying = 0;
				}
			},
			async toggleSourcePlay() {
				if (this.isSourcePlaying) {
					this.stopAudio();
					this.isSourcePlaying = 0;
					return;
				}

				try {
					this.isSourcePlaying = 2;
					this.$player = new Audio();
					this.$player.src = this.sourceBase64;
					this.$player.play();
					this.$player.addEventListener('ended', () => {
						delete this.$player;
						this.isSourcePlaying = 0;
					}, false);
				} catch {
					this.isSourcePlaying = 0;
				}
			},
			stopAudio() {
				if (this.$player) {
					this.$player.pause();
				}
			},
			close() {
				this.stopRecording(true);
			},
			save() {
				this.$emit('save', this.audioLink);
				this.popupVisible = false;
			},
			show() {
				if (this.recorder.listeningMic.value) {
					this.initRecording(true);
				}
				this.popupVisible = false;
				this.sourceBase64 = null;
				this.ssml = null;
				this.ssmlBase64 = null;
				setImmediate(() => this.popupVisible = true);
			},
			initRecording(preventRecord) {
				if (__DEBUG__)
					console.debug('initRecording');
				this.uploadedFile = null;
				this.stopRecording(true);
				var constraints = { audio: {
					mandatory: {
						googEchoCancellation: false
					},
					optional: []
				}, video: false };
				return new Promise(res => navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
					this.recorder.stream = stream;
					var ctx = this.recorder.audioContext;
					var input = ctx.createMediaStreamSource(stream);
					this.recorder.input = input;

					/* Stereo analyser */
					var stereoAnalyser = new audioHelper.StereoAnalyserNode(ctx);
					stereoAnalyser.fftSize = 512;
					this.recorder.stereoAnalyser = stereoAnalyser;

					if (this.recorder.listeningMic.value) {
						input.connect(stereoAnalyser);
						stereoAnalyser.connect(ctx.destination);
					} else {
						input.connect(stereoAnalyser);
					}

					this.recorder.instance = new Recorder(input, { numChannels: 1, bufferLen: this.$globals.recorderJs.bufferLen }, recorderWorker);
					if (!preventRecord)
						this.recorder.instance.record();

					setImmediate(() => {
						res();
					});
				}).catch((ex) => {
					res();
					throw(ex);
				}));
			},
			async processBlob(blob) {
				this.recorder.processing = true;
				this.ssml = null;
				try {
					this.sourceBase64 = await this.$Data.blob2Base64(blob);
					const voice = this.voice?.id || 'en-US-GuyNeural';
					const language = this.voice?.locale || 'en-US';
					
					const azureFile = await this.$azure.uploadBlobWithInfoResponse(`voice_conversion_${this.$Data.uuid()}.wav`, blob, 'other');
					this.audioLink = `${azureFile?.AzureFileUrl}`;

					if (__DEBUG__) {
						console.debug('Voice conversion', { file: azureFile, lnk: this.audioLink });
					}

					this.ssml = `<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${language}">` +
						`<voice name="${voice}"><mstts:voiceconversion url="${this.$Data.encodeHTML(this.audioLink)}"/></voice></speak>`;

					const r = await this.$Data.postExternalApi('Creative/BuildAudioCustom', {
						Language: language,
						Rate: 1,
						Pitch: 0,
						Voice: voice,
						Ssml: this.ssml,
						ClientProcessId: this.$auth.getGlobalProcessId()
					}).catch(() => {});

					this.ssmlBase64 = r?.AudioData && `data:audio/wav;base64,${r?.AudioData}`;
				} finally {
					this.recorder.processing = false;
				}
			},
			async toggleRecord() {
				if (!this.recorder.isRecording && !this.voice?.isActive) {
					this.$confirm(void 0, this.$tCaption('The voice you have selected is in sleep mode.<br>Voice conversion highly depends on the voice used.<br>You can wait for the voice to come online or use a non-premium voice.'),{
						buttons: [{
							text: this.$tCaption('Wait for voice activation', '@actions'),
							onClick: () => { 
								this.voice.__activate();
							},
							type: 'success'
						}, {
							text: this.$tCaption('Cancel', '@actions'),
							onClick: () => {}
						}]
					});
					return;
				}
				this.recorder.isRecording = !this.recorder.isRecording;
				if (this.recorder.isRecording) {
					this.initRecording();
				} else {
					const blob = await this.stopRecording();
					await this.processBlob(blob);
				}
			},
			async stopRecording(preventSave) {
				this.stopAudio();
				try {
					if (this.recorder.instance) {
						this.recorder.stream.getAudioTracks()[0].stop();
						this.recorder.instance.stop();
						this.recorder.input.disconnect();
					}

					if (!preventSave) {
						return new Promise((res) => this.recorder.instance.exportWAV((blob) => {
							const resampled = audioHelper.resampleBlob(blob, 16000, { mono: true });
							return res(resampled);
						}));
					}
				} catch(ex) {
					console.error(ex);
				}
			}
		}
	}
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.ssml-insertion--voice-conversion-tool {
		.record-button {
			border-radius: 4px;
		}
	}
</style>
